import { Injectable } from '@angular/core';
import { Months } from 'app/shared/data/months.data';
import { DaysOfWeek } from 'app/shared/data/daysOfWeek.data';
import { LeyendColor } from 'app/shared/data/leyend-color.data';
import { HolidayService } from '../holiday.service';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

@Injectable()
export class CalendarDatatableService {
  private currentDate: Date
  private days: any[]
  private currentMonth: string
  private currentYear: number
  private isMonthMode: boolean
  private today: string
  private holidays: string[]

  /**
  * Constructor
  *
  * @param {HolidayService} holidayService
  * 
  */
  constructor(
    private holidayService: HolidayService
  ) {
    this.currentDate = new Date()

    this.today = this.dateIsoToString(this.currentDate)

    this.isMonthMode = true
  }

  private getAllDays(): any {
    const now: Date = this.currentDate
    const month = now.getMonth()
    const days = this.getDaysFromDate(now, month)
    const daysElements = this.addDays(days)

    this.days = daysElements
    this.currentMonth = Months[month]
    this.currentYear = now.getFullYear()

    return {
      days: this.days,
      currentMonth: this.currentMonth,
      currentYear: this.currentYear,
      isMonthMode: this.isMonthMode
    }
  }

  private getDaysFromDate(date: Date, month: number): any[] {
    return this.isMonthMode ? this.getDaysOfMonth(date, month) : this.getDaysOfWeek(date, month)
  }

  private getDaysOfMonth(date: Date, month: number): any[] {
    let firstOfTheMonth = new Date(date.getFullYear(), month, 1)

    let days = []

    return this.setDaysElements(firstOfTheMonth, days, () => month === firstOfTheMonth.getMonth())
  }

  private getDaysOfWeek(date: Date, month: number): any[] {
    const dayOfWeek = date.getDay()

    const daysDeducted = dayOfWeek === 0 ? 6 : dayOfWeek - 1

    let firstOfTheWeek = new Date(date.getFullYear(), month, date.getDate() - daysDeducted)

    let days = []

    return this.setDaysElements(firstOfTheWeek, days, () => days.length !== 7)
  }

  private setDaysElements(date: Date, days: any[], condition: () => boolean): any[] {
    while (condition()) {
      const dateParsed = this.getIsoFormatFromDate(date)

      days.push({
        day: dateParsed.day,
        month: dateParsed.month,
        year: dateParsed.year
      })

      date.setDate(parseInt(dateParsed.day) + 1)
    }

    return days
  }

  private addDays(days: any[]): any[] {
    let daysElements = []

    for (let day of days) {
      const newDay = day['day']
      const newMonth = day['month']
      const newYear = day['year']

      const stringDate = `${newYear}-${newMonth}-${newDay}`

      const dayClass = this.getDayClass(stringDate)

      const dayOfWeek = this.getLetterOfWeek(stringDate)

      daysElements.push({
        name: `${dayOfWeek.initial}-${newDay}`,
        day: newDay,
        dayOfWeek: dayOfWeek.day,
        prop: stringDate,
        class: dayClass
      })
    }

    return daysElements
  }

  private getDayClass(stringDate: string): string {

    if (this.checkIfDayIsToday(stringDate)) {
      return 'cell-width-today'
    }

    if (this.checkIfDayIsHoliday(stringDate)) {
      return 'cell-width-holiday'
    }

    if (this.checkIfDayIsWeekend(stringDate)) {
      return 'cell-width-weekend'
    }

    return 'cell-width'
  }

  private checkIfDayIsToday(stringDate: string): boolean {
    return stringDate === this.today
  }

  private checkIfDayIsHoliday(stringDate: string): boolean {
    return this.holidays.includes(this.getDayOfHoliday(stringDate))
  }

  private checkIfDayIsWeekend(stringDate: string): boolean {
    const date: Date = new Date(stringDate)
    const dayOfWeek = date.getDay()

    return dayOfWeek === 0 || dayOfWeek === 6
  }

  private getLetterOfWeek(stringDate: string): any {
    const now: Date = new Date(stringDate)

    const numberOfDayOfWeek = now.getDay()

    const dayOfWeek = DaysOfWeek[numberOfDayOfWeek]

    const day = {
      initial: dayOfWeek === 'Miércoles' ? 'X' : dayOfWeek[0],
      day: dayOfWeek
    }

    return day
  }

  public getdaysInfo(): string {
    let daysInfo = this.currentMonth

    if (!this.isMonthMode) {
      const days = this.days

      const firstDay: string = days[0].day
      const lastDay: string = days[days.length - 1].day

      let months: number[] = []

      days.forEach((item) => {
        const month: number = parseInt(item.prop.split('-')[1])

        if (!months.includes(month)) {
          months.push(month)
        }
      })

      daysInfo = months.length === 1
        ? `${firstDay} - ${lastDay} ${this.currentMonth}`
        : `${firstDay} ${Months[months[0] - 1]} - ${lastDay} ${Months[months[1] - 1]}`
    }

    return daysInfo
  }


  public activateWeekView(): any {
    this.isMonthMode = false

    return this.getAllDays()
  }

  public activateMonthView(): any {
    this.isMonthMode = true

    return this.getAllDays()
  }

  public getToday(): any {
    this.currentDate = new Date()

    return this.getAllDays()
  }

  public getPrevDate(): any {
    const currentMonth = this.currentDate.getMonth()
    const currentDate = this.currentDate.getDate()

    this.currentDate.setDate(1)

    this.isMonthMode
      ? this.currentDate.setMonth(currentMonth - 1)
      : this.currentDate.setDate(currentDate - 7)

    return this.getAllDays()
  }

  public getNextDate(): any {
    const currentMonth = this.currentDate.getMonth()
    const currentDate = this.currentDate.getDate()

    this.currentDate.setDate(1)

    this.isMonthMode
      ? this.currentDate.setMonth(currentMonth + 1)
      : this.currentDate.setDate(currentDate + 7)

    return this.getAllDays()
  }

  private getIsoFormatFromDate(date: Date): any {
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()

    return { year, month, day }
  }

  public dateIsoToString(date: Date): string {
    let newDate = this.getIsoFormatFromDate(date)

    return `${newDate.year}-${newDate.month}-${newDate.day}`
  }

  public getLeyendColor(eventStatus: string): string {
    return LeyendColor[eventStatus]
  }

  public setHolidays(): Observable<string[]> {
    return this.holidayService.getHolidays().pipe(
      map((data) => {
        this.holidays = data.map(item => this.getDayOfHoliday(item))

        return this.getAllDays()
      })
    )
  }

  public getHolidays(): any {
    return this.holidays
  }

  public calculateEmployeesWidthColum(employees: any[]): number {
    const canvas = document.createElement('canvas')

    const context = canvas.getContext('2d')
    context.font = 'bold 14px Roboto, Arial'

    const longestEmployee = employees.reduce((a, b) => {
      const aName = this.getEmployeeFullName(a)
      const bName = this.getEmployeeFullName(b)

      return aName.length > bName.length ? a : b
    })

    const longestName = this.getEmployeeFullName(longestEmployee)

    const width = context.measureText(longestName).width

    return width + 60
  }

  private getEmployeeFullName(employee: any): string {
    const name = employee?.name ?? employee?.employee?.name
    const surname = employee?.last_name ?? employee?.employee?.last_name ?? ''

    return `${name} ${surname}`
  }


  private getDayOfHoliday(fullDay: string): string {
    const [year, month, day] = fullDay.split('-')

    return `${month}-${day}`
  }

  public fireSwalAlert(options: SweetAlertOptions): Promise<SweetAlertResult> {
    return swal.fire(options)
  }
}
